import produce from 'immer';

import {ClientId, FromServerId, NLOToServerId, ToServerId} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {addCid} from 'notebook/store/StoreTransformUtil';
import {GenericNoteLinkedScratchPlateDesign} from 'notebook/util/NoteUtilConstants';
import uniqueId from 'util/uniqueId';

const SCRATCH_PLATE_DESIGN_CID_PREFIX = 'scratch-plate-design-';
const SCRATCH_PLATE_CID_PREFIX = 'scratch-plate-';

export type FromServerNoteLinkedScratchPlateDesign = GenericNoteLinkedScratchPlateDesign<FromServerId>;
export type NoteLinkedScratchPlateDesign = GenericNoteLinkedScratchPlateDesign<ClientId>;
export type ToServerNoteLinkedScratchPlateDesign = GenericNoteLinkedScratchPlateDesign<ToServerId>;

export function copyNoteLinkedScratchPlateDesignForPaste(
  source: NoteLinkedScratchPlateDesign
): NoteLinkedScratchPlateDesign {
  return produce(source, (draft) => {
    draft.cid = uniqueId(SCRATCH_PLATE_DESIGN_CID_PREFIX);
    draft.data.scratchPlateCid = uniqueId(SCRATCH_PLATE_CID_PREFIX);
    draft.data.sourceScratchPlateId = draft.data.sourceScratchPlateId ?? draft.data.scratchPlateId;
  });
}

/**
 * Transform {@link FromServerNoteLinkedScratchPlateDesign} into a {@link NoteLinkedScratchPlateDesign}
 *
 * Invoked when loading a note into the document store. Replaces the server id with a cid,
 * which is rehydrated on save via {@link prepareForSave}.
 */
export function prepareForStore(item: FromServerNoteLinkedScratchPlateDesign, cidIdMap: CidIdMap): void {
  addCid(item, SCRATCH_PLATE_DESIGN_CID_PREFIX, cidIdMap);
}

/**
 * Transform a {@link NoteLinkedScratchPlateDesign} back into a server-ready representation.
 *
 * Invoked when saving a note. Rehydrates the server id from the cidIdMap.
 */
export function prepareForSave(
  item: NoteLinkedScratchPlateDesign,
  cidIdMap: CidIdMap
): ToServerNoteLinkedScratchPlateDesign {
  const scratchPlateId =
    item.data.scratchPlateCid !== undefined ? cidIdMap[item.data.scratchPlateCid] : undefined;
  return {
    id: cidIdMap[item.cid] as NLOToServerId['id'],
    cid: item.cid,
    type: item.type,
    name: item.name,
    data: {
      scratchPlateId: typeof scratchPlateId === 'string' ? scratchPlateId : item.data.scratchPlateId,
      scratchPlateName: item.data.scratchPlateName,
      ...(typeof scratchPlateId !== 'string' && item.data.sourceScratchPlateId !== undefined
        ? {
            scratchPlateCid: item.data.scratchPlateCid,
            sourceScratchPlateId: item.data.sourceScratchPlateId,
          }
        : {}),
    },
  };
}
